import { supabase } from './supabaseService';
import { logger } from '../utils/logger';

export interface Notification {
  id: string;
  user_id: string | null;
  code: string;
  title: string;
  body: string;
  data: Record<string, any>;
  severity: 'info' | 'warn' | 'critical';
  read_at: Date | null;
  created_at: Date;
  deep_link?: string | null;
}

export type NotificationGroup = 'ventas' | 'cobros' | 'colaboracion' | 'sistema';
export type PrefChannel = 'in_app' | 'email';

export interface NotificationPreference {
  id: string;
  userId: string;
  groupKey: NotificationGroup;
  channel: PrefChannel;
  enabled: boolean;
  updatedAt: Date;
}

export const NOTIFICATION_GROUPS: Array<{ key: NotificationGroup; label: string; description: string }> = [
  { key: 'ventas', label: 'Ventas', description: 'Leads, contactos, empresas, reuniones y actividades' },
  { key: 'cobros', label: 'Cobros', description: 'Partes de horas, facturas, pagos e ingresos Qonto' },
  { key: 'colaboracion', label: 'Colaboración', description: 'Colaboradores, contratos y documentos firmados' },
  { key: 'sistema', label: 'Sistema', description: 'Usuarios, proyectos y avisos del sistema' },
];

const codeToGroup = (code: string): NotificationGroup => {
  if (code.startsWith('LEAD.') || code.startsWith('CONTACT.') || code.startsWith('COMPANY.')
      || code.startsWith('MEETING.') || code.startsWith('MEET.') || code.startsWith('ACTIVITY.')
      || code.startsWith('FORM.')) {
    return 'ventas';
  }
  if (code.startsWith('TIMESHEET.') || code.startsWith('INVOICE.') || code.startsWith('PAYMENT.')
      || code.startsWith('QONTO.')) {
    return 'cobros';
  }
  if (code.startsWith('COLLABORATOR.') || code.startsWith('CONTRACT.') || code.startsWith('DOC.')) {
    return 'colaboracion';
  }
  return 'sistema';
};

const DEEP_LINK_MAP: Record<string, string> = {
  'LEAD.NEW': '/leads',
  'LEAD.ASSIGN': '/leads',
  'LEAD.CONVERTED': '/contactos',
  'CONTACT.CREATED': '/contactos',
  'COMPANY.CREATED': '/contactos',
  'MEETING.SCHEDULED': '/reuniones',
  'MEETING.APPROVED': '/reuniones',
  'MEETING.CANCELLED': '/reuniones',
  'MEETING.APPROVAL_REQUIRED': '/reuniones',
  'MEET.APPROVAL_REQUIRED': '/reuniones',
  'ACTIVITY.CREATED': '/actividades',
  'FORM.SUBMISSION': '/formularios',
  'FORM.RESPONSE': '/formularios',
  'TIMESHEET.SUBMIT': '/pagos',
  'TIMESHEET.APPROVE': '/pagos',
  'TIMESHEET.APPROVED': '/pagos',
  'TIMESHEET.REJECT': '/pagos',
  'INVOICE.COLLAB.UPLOAD': '/pagos',
  'INVOICE.CREATED': '/pagos',
  'INVOICE.UPDATED': '/pagos',
  'INVOICE.VALIDATED': '/pagos',
  'INVOICE.REJECTED': '/pagos',
  'INVOICE.PAID': '/pagos',
  'PAYMENT.SCHEDULED': '/pagos',
  'PAYMENT.DUE': '/pagos',
  'PAYMENT.SENT': '/pagos',
  'INVOICE.OVERDUE': '/pagos',
  'QONTO.TX.INCOME': '/pagos',
  'QONTO.INCOME.UNASSIGNED': '/pagos',
  'QONTO.BANK.TX_IN': '/pagos',
  'QONTO.BANK.TX_OUT': '/pagos',
  'COLLABORATOR.CREATED': '/colaboradores',
  'COLLABORATOR.UPDATED': '/colaboradores',
  'CONTRACT.SIGNED': '/mantenimiento',
  'PROPOSAL.ACCEPTED': '/presupuestos',
  'USER.CREATED': '/usuarios',
  'PROJECT.UPDATED': '/proyectos',
  'PROJECT.STATUS_CHANGED': '/proyectos',
  'PROJECT.ASSIGNED': '/pagos',
  'PROJECT.CREATED': '/proyectos',
  'TASK.STATUS_CHANGED': '/pagos',
  'SYSTEM.UPDATE': '/dashboard',
};

class NotificationService {
  private generateDeepLink(code: string): string {
    return DEEP_LINK_MAP[code] || '/dashboard';
  }

  private async isChannelEnabled(userId: string, code: string, channel: PrefChannel): Promise<boolean> {
    try {
      const group = codeToGroup(code);
      const { data } = await supabase
        .from('notification_preferences')
        .select('enabled')
        .eq('user_id', userId)
        .eq('group_key', group)
        .eq('channel', channel)
        .maybeSingle();
      return data ? data.enabled : true;
    } catch {
      return true;
    }
  }

  async createNotification(payload: {
    user_id?: string | null;
    code: string;
    title: string;
    body: string;
    data?: Record<string, any>;
    severity: 'info' | 'warn' | 'critical';
  }): Promise<void> {
    try {
      // Semántica: undefined o null → broadcast (user_id = null en DB).
      // Sólo se targeta al usuario cuando quien crea la notificación pasa
      // un ID explícito. Antes había un fallback a auth.user.id que
      // convertía cualquier "notif sin destino" en autonotificación del
      // creador.
      const targetUserId = payload.user_id ?? null;

      if (targetUserId) {
        const inApp = await this.isChannelEnabled(targetUserId, payload.code, 'in_app');
        if (!inApp) {
          logger.debug('Notificación in-app omitida por preferencias', { code: payload.code });
          return;
        }
      }

      const deep_link = this.generateDeepLink(payload.code);
      const priorityMap: Record<string, string> = { info: 'medium', warn: 'high', critical: 'high' };

      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: targetUserId,
          type: payload.code,
          code: payload.code,
          title: payload.title,
          body: payload.body,
          payload: payload.data || {},
          data: payload.data || {},
          priority: priorityMap[payload.severity] || 'medium',
          severity: payload.severity,
          deep_link,
          read_at: null,
        })
        .select()
        .single();

      if (error) throw error;
      logger.debug('Notificación creada', { id: data.id, code: payload.code });

      if ('Notification' in window && Notification.permission === 'granted' && targetUserId) {
        new Notification(payload.title, {
          body: payload.body,
          icon: '/logo-ceroideas.png',
          data: payload.data,
        });
      }
    } catch (error) {
      logger.error('Error creando notificación', error);
      throw error;
    }
  }

  async getUserNotifications(limit: number = 50, includeRead: boolean = false): Promise<Notification[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from('notifications')
        .select('*')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (!includeRead) query = query.is('read_at', null);

      const { data: notifications, error } = await query;
      if (error) throw error;

      return (notifications || []).map((n: any) => ({
        id: n.id,
        user_id: n.user_id,
        code: n.code || n.type || 'UNKNOWN',
        title: n.title,
        body: n.body,
        data: n.data || n.payload || {},
        severity: n.severity || 'info',
        read_at: n.read_at ? new Date(n.read_at) : null,
        created_at: new Date(n.created_at),
        deep_link: n.deep_link || this.generateDeepLink(n.code || n.type),
      }));
    } catch (error) {
      logger.error('Error cargando notificaciones', error);
      return [];
    }
  }

  async markAsRead(notificationId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId);
    if (error) throw error;
  }

  async markAllAsRead(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .or(`user_id.eq.${user.id},user_id.is.null`)
      .is('read_at', null);
    if (error) throw error;
  }

  async notifyNewLead(leadName: string, assignedUserId?: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId ?? null,
      code: 'LEAD.NEW',
      title: 'Nuevo Lead Recibido',
      body: `${leadName} ha enviado una consulta`,
      data: { leadName },
      severity: 'info',
    });
  }

  async notifyMeetingScheduled(meetingTitle: string, contactName: string, assignedUserId: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId,
      code: 'MEETING.APPROVAL_REQUIRED',
      title: 'Reunión Pendiente de Aprobación',
      body: `${contactName} solicita reunión: ${meetingTitle}`,
      data: { meetingTitle, contactName },
      severity: 'warn',
    });
  }

  async notifyContactCreated(contactName: string, company: string, assignedUserId?: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId,
      code: 'CONTACT.CREATED',
      title: 'Nuevo Cliente Creado',
      body: `${contactName} de ${company} ha sido añadido al CRM`,
      data: { contactName, company },
      severity: 'info',
    });
  }

  async notifyCompanyCreated(companyName: string, assignedUserId?: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId,
      code: 'COMPANY.CREATED',
      title: 'Nueva Empresa Creada',
      body: `La empresa ${companyName} ha sido añadida al CRM`,
      data: { companyName },
      severity: 'info',
    });
  }

  async notifyLeadConverted(leadName: string, company: string, assignedUserId?: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId,
      code: 'LEAD.CONVERTED',
      title: 'Lead Convertido a Cliente',
      body: `${leadName} de ${company} ha sido convertido exitosamente`,
      data: { leadName, company },
      severity: 'info',
    });
  }

  async notifyUserCreated(userName: string, userEmail: string, assignedUserId?: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId,
      code: 'USER.CREATED',
      title: 'Nuevo Usuario Creado',
      body: `${userName} (${userEmail}) ha sido añadido al sistema`,
      data: { userName, userEmail },
      severity: 'info',
    });
  }

  async notifyActivityCreated(activityType: string, contactName: string, assignedUserId?: string): Promise<void> {
    await this.createNotification({
      user_id: assignedUserId,
      code: 'ACTIVITY.CREATED',
      title: 'Nueva Actividad Registrada',
      body: `${activityType} registrada para ${contactName}`,
      data: { activityType, contactName },
      severity: 'info',
    });
  }

  async notifyQontoIncome(amount: number, counterparty: string, needsAssignment: boolean = false): Promise<void> {
    const code = needsAssignment ? 'QONTO.INCOME.UNASSIGNED' : 'QONTO.BANK.TX_IN';
    const title = needsAssignment ? 'Ingreso Sin Asignar' : 'Nuevo Ingreso';
    const body = needsAssignment
      ? `${amount}€ de ${counterparty} necesita asignación`
      : `Abono ${amount}€ de ${counterparty}`;

    await this.createNotification({
      user_id: null,
      code,
      title,
      body,
      data: { amount, counterparty },
      severity: needsAssignment ? 'warn' : 'info',
    });
  }

  async getUserPreferences(userId: string): Promise<NotificationPreference[]> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId);
    if (error) throw error;

    return (data || []).map((row: any) => ({
      id: row.id,
      userId: row.user_id,
      groupKey: row.group_key,
      channel: row.channel,
      enabled: row.enabled ?? true,
      updatedAt: new Date(row.updated_at),
    }));
  }

  async updateUserPreference(
    userId: string,
    groupKey: NotificationGroup,
    channel: PrefChannel,
    enabled: boolean
  ): Promise<void> {
    const { error } = await supabase
      .from('notification_preferences')
      .upsert(
        { user_id: userId, group_key: groupKey, channel, enabled, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,group_key,channel' }
      );
    if (error) throw error;
  }
}

export const notificationService = new NotificationService();
